import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR, whatsappLink } from "@/data/catalog";

const buildWhatsAppOrder = (items, total) => {
  const lines = items.map((i) => `• ${i.name} x ${i.qty} — ${formatINR(i.price * i.qty)}`);
  return whatsappLink(
    `Namaste! I want to order from Eleven Stars:\n\n${lines.join("\n")}\n\nTotal: ${formatINR(total)}\n\nPlease confirm my order.`
  );
};

export const CartDrawer = () => {
  const { cartOpen, setCartOpen, items, setQty, removeItem, total, setCheckoutOpen } = useCart();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#F9F6F0] flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-[#1A4331] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div data-testid="cart-empty" className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-16">
            <ShoppingBag className="w-12 h-12 text-[#7C9A82]" />
            <p className="text-[#4B5563]">Your cart is empty.</p>
            <Button
              data-testid="cart-shop-now"
              variant="outline"
              className="rounded-full border-[#1A4331]/40 text-[#1A4331]"
              onClick={() => {
                setCartOpen(false);
                document.getElementById("products")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {items.map((item) => (
                <div
                  key={item.slug}
                  data-testid={`cart-item-${item.slug}`}
                  className="flex gap-3 bg-white rounded-xl border border-[#E5E0D8] p-3"
                >
                  <div className="w-20 h-20 rounded-lg bg-[#F9F6F0] p-1.5 shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1c2826] truncate">{item.name}</p>
                    <p className="text-xs text-[#4B5563]">{item.pack}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`qty-minus-${item.slug}`}
                          onClick={() => setQty(item.slug, item.qty - 1)}
                          className="w-7 h-7 rounded-full border border-[#E5E0D8] flex items-center justify-center text-[#1A4331] hover:bg-[#1A4331] hover:text-white transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span data-testid={`qty-${item.slug}`} className="text-sm font-semibold w-5 text-center">
                          {item.qty}
                        </span>
                        <button
                          data-testid={`qty-plus-${item.slug}`}
                          onClick={() => setQty(item.slug, item.qty + 1)}
                          className="w-7 h-7 rounded-full border border-[#E5E0D8] flex items-center justify-center text-[#1A4331] hover:bg-[#1A4331] hover:text-white transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="font-bold text-[#1A4331]">{formatINR(item.price * item.qty)}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-${item.slug}`}
                    onClick={() => removeItem(item.slug)}
                    className="self-start text-[#4B5563] hover:text-[#C05C46] transition-colors"
                    aria-label="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E5E0D8] pt-4 space-y-3">
              <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                <Truck className="w-4 h-4 text-[#7C9A82]" />
                Shipping across India · Secure online payment via Razorpay
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#4B5563]">Subtotal</span>
                <span data-testid="cart-total" className="text-xl font-bold text-[#1A4331]">
                  {formatINR(total)}
                </span>
              </div>
              <Button
                data-testid="proceed-to-checkout"
                onClick={() => {
                  setCartOpen(false);
                  setCheckoutOpen(true);
                }}
                className="w-full rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white py-6 text-base font-semibold"
              >
                Proceed to Checkout
              </Button>
              <a
                data-testid="order-on-whatsapp"
                href={buildWhatsAppOrder(items, total)}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full rounded-full border-2 border-[#25D366] text-[#1a8f47] py-3 text-sm font-semibold hover:bg-[#25D366] hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                </svg>
                Order on WhatsApp
              </a>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
